import axios from 'axios';
import { isTokenExpired } from './utils/jwt';

// Create a separate instance for unauthenticated API calls that need JSON
const analyzeApi = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://proto-backend-d2c1638486b9.herokuapp.com',
    timeout: 60000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    withCredentials: false
});

// Create a separate instance for unauthenticated API calls that need FormData (no default headers)
const publicApi = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://proto-backend-d2c1638486b9.herokuapp.com',
    timeout: 60000,
    withCredentials: false
});

// Main API instance for authenticated calls
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || 'https://proto-backend-d2c1638486b9.herokuapp.com'
});

// Add request interceptor to include token
api.interceptors.request.use(config => {
    // Skip token check for unauthenticated endpoints
    if (config.url === '/api/analyze-text' ||
        config.url?.includes('/api/montage/') ||
        config.url?.includes('/api/chromatic/')) {
        return config;
    }

    const token = localStorage.getItem('token');
    if (token && !isTokenExpired(token)) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
}, error => {
    return Promise.reject(error);
});

// Add response interceptor for debugging
api.interceptors.response.use(
    response => {
        console.log('Response received:', response.status);
        return response;
    },
    error => {
        // Only redirect for authenticated routes
        if (error.response?.status === 401 && !error.config.url.includes('/api/analyze-text')) {
            console.log('Token expired or invalid, redirecting to landing page');
            localStorage.removeItem('token');
            window.location.href = '/';
        }
        console.error('Response error:', error.response?.status);
        return Promise.reject(error);
    }
);

// API methods
const apiMethods = {
    // Health check (unauthenticated)
    health: () => analyzeApi.get('/health', { timeout: 15000 }),
    // Public user count (unauthenticated, for landing page)
    getUserCount: () => publicApi.get('/api/users/count'),
    // Unauthenticated routes
    analyzeText: (data) => analyzeApi.post('/api/analyze-text', { 
        text: data.text,
        personName: data.personName 
    }),

    analyzeShortText: (data) => analyzeApi.post('/api/analyze-short-text', { 
        text: data.text,
        personName: data.personName 
    }),

    // Montage routes (unauthenticated)
    getMontageUploadUrls: (clipCount) => publicApi.post('/api/montage/upload-urls', { clipCount }),
    confirmMontageUpload: (montageId, uploadedKeys) => publicApi.post('/api/montage/confirm', { montageId, uploadedKeys }),
    getMontageStatus: (montageId) => publicApi.get(`/api/montage/status/${montageId}`),
    getMontageDownloadUrl: (montageId) => publicApi.get(`/api/montage/${montageId}/download-url`),

    // Chromatic / optical treatment (unauthenticated)
    processChromaticVideo: (formData) => publicApi.post('/api/chromatic/process', formData, {
        timeout: 600000,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
    }),
    getChromaticStatus: (jobId) => publicApi.get(`/api/chromatic/status/${jobId}`, {
        // Cache-bust only via query param — custom Cache-Control/Pragma headers
        // trigger CORS preflight and are blocked (allowedHeaders is Content-Type/Authorization).
        params: { _t: Date.now() },
    }),
    getChromaticDownloadUrl: (jobId) => publicApi.get(`/api/chromatic/${jobId}/download-url`),

    createCheckoutSessionForAnalysis: () => api.post('/api/create-checkout-session-for-analysis'),
    checkPaymentStatus: (data) => api.post('/api/check-payment-status', data),

    // Profile routes
    getUserProfile: () => api.get('/api/user-profile'),
    updateProfile: (profileData) => api.post('/api/update-profile', profileData),
    uploadProfilePicture: (formData) => api.post('/api/upload-profile-picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    updateProspectingProfile: (prospectingProfile) => api.post('/api/update-prospecting-profile', prospectingProfile),
    markVisited: () => api.post('/api/mark-visited'),
    getUserFiles: () => api.get('/api/files'),
    getVoices: () => api.get('/api/voices'),

    // Voice related routes
    generateSpeech: (text, voiceId, prospectId, forceNew = false) => 
        api.post('/api/text-to-speech', { text, voiceId, prospectId, forceNew }),
    cloneVoice: (formData) => api.post('/api/clone-voice', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    testClonedVoice: (text, voiceId) => api.post('/api/test-cloned-voice', { text, voiceId }),
    cloneWorkspaceVoice: (workspaceId, formData) => api.post(`/api/workspace/${workspaceId}/clone-voice`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    testWorkspaceClonedVoice: (workspaceId, text, voiceId) => api.post(`/api/workspace/${workspaceId}/test-cloned-voice`, { text, voiceId }),

    // File and video handling routes
    saveVideos: (formData) => api.post('/api/save-videos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        timeout: 120000
    }),
    getUserVideos: () => api.get('/api/user/videos'),
    saveFile: (formData) => api.post('/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),

    // LinkedIn prospects routes
    scrapeLinkedInProfile: (linkedinUrl, userName, userPicture) => 
        api.post('api/prospects/scrape-profile', { linkedinUrl, userName, userPicture }),
    getLinkedInProspects: () => api.get('/api/prospects'),
    deleteProspect: (prospectId) => api.delete(`/api/prospects/${prospectId}`),
    generatePitch: (prospectId, isTextOnly, language) => 
        api.post(`/api/prospects/generate-pitch/${prospectId}`, { isTextOnly, language }),
    updateProspectPitch: (prospectId, pitch) => 
        api.patch(`/api/prospects/${prospectId}/pitch`, { pitch }),
    extractLinkedInInfo: (formData) => api.post('/api/prospects/extract-linkedin-info', formData, {
        headers: { 'Accept': 'application/json' },
        timeout: 30000,
        maxBodyLength: Infinity,
        maxContentLength: Infinity
    }),

    // S3 and upload related routes
    getUploadUrl: (fileType, videoCategory) => 
        api.post('/api/get-upload-url', { fileType, videoCategory }),
    uploadToS3: async (url, file) => {
        try {
            return await axios.put(url, file, {
                headers: { 'Content-Type': file.type },
                maxContentLength: Infinity,
                maxBodyLength: Infinity
            });
        } catch (error) {
            console.error('S3 upload error:', error.message);
            throw error;
        }
    },
    confirmUpload: (key, videoCategory) => 
        api.post('/api/confirm-upload', { key, videoCategory }),
    // deleteVideo: (category) => 
    //     api.delete(`/api/videos/${category}`),

    // Recording routes
    saveRecording: (formData) => api.post('/api/save-recording', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteRecording: (recordingId) => api.delete(`/api/recording/${recordingId}`),
    uploadAudioForVoiceCloning: (formData) => api.post('/api/upload-audio-for-voice-cloning', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),

    // Video generation routes
    // commented out because we are using the new video studio
    // generateVideo: (data) => api.post('/api/generate', data),
    // checkVideoStatus: (prospectId) => api.get(`/api/videos/status/${prospectId}`),

    // Payment and promo code routes
    validatePromoCode: (code) => api.post('/api/validate-promo-code', { code }),
    createPortalSession: (workspaceId) => api.post('/api/create-portal-session', { workspaceId }),
    createCheckoutSession: (plan, workspaceId) => api.post('/api/create-checkout-session', { plan, workspaceId }),
    getSubscriptionStatus: (sessionId) => api.get('/api/subscription-status', { params: { session_id: sessionId } }),
    confirmSubscription: (sessionId) => api.post('/api/confirm-subscription', { session_id: sessionId }),

    // Runway routes
    transformVideo: (formData) => api.post('/api/transform-video', formData, {
        // headers: { 'Content-Type': 'multipart/form-data' }, // Don't set Content-Type header - let axios set it with the boundary
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        timeout: 300000 // 5 minutes timeout
    }),
    
    // Check the status of a video generation task
    checkTransformVideoStatus: (taskId) => api.get(`/api/transform-video/status/${taskId}`),

    // New method for stitching videos
    stitchVideo: (formData) => api.post('/api/stitch-video', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),

    // Get user credits
    getUserCredits: () => api.get('/api/user/credits'),
    addCredits: (data) => api.post('/api/user/credits/add', data),

    // News routes
    getNewsArticles: () => api.get('/api/news'),
    createNewsArticle: (data) => api.post('/api/news', data),
    getLatestNewsArticle: () => api.get('/api/news/latest'),
    dismissNewsArticle: (articleId) => api.post(`/api/news/dismiss/${articleId}`),

    // Admin dashboard routes
    getVideoGenerations: () => api.get('/api/admin/video-generations'),
    getVideoUrl: (type, videoId) => `/api/videos/${type}/${videoId}`,

    // Video Management routes
    uploadVideo: (formData, config = {}) => api.post('/api/videos/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'Accept': 'application/json'
        },
        ...config
    }),

    // Admin video upload for specific user
    adminUploadVideo: (userId, formData, config = {}) => api.post(`/api/admin/users/${userId}/upload-video`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'Accept': 'application/json'
        },
        timeout: 5400000, // 90 minutes to match backend timeout for large files
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        ...config
    }),

    getVideos: (params) => {
        // params: { page, limit }
        if (params && (params.page || params.limit)) {
            const search = new URLSearchParams();
            if (params.page) search.set('page', params.page);
            if (params.limit) search.set('limit', params.limit);
            return api.get(`/api/videos?${search.toString()}`);
        }
        return api.get('/api/videos');
    },
    
    updateVideoMetadata: (videoId, metadata) => 
        api.patch(`/api/videos/${videoId}/metadata`, metadata),
    
    deleteVideo: (videoId) => 
        api.delete(`/api/videos/${videoId}/delete`),
    
    addVideoTag: (videoId, tag) => 
        api.post(`/api/videos/${videoId}/tags`, { tag }),
    
    removeVideoTag: (videoId, tag) => 
        api.delete(`/api/videos/${videoId}/tags/${encodeURIComponent(tag)}`),
    
    updateVideoDescription: (videoId, description) => 
        api.patch(`/api/videos/${videoId}/description`, { description }),

    // Admin BackOffice routes
    getAdminUsers: () => api.get('/api/admin/users'),
    getAdminUserDetails: (userId) => api.get(`/api/admin/users/${userId}`),
    getAdminVideos: (params = {}) => {
        const search = new URLSearchParams();
        if (params.page) search.set('page', params.page);
        if (params.limit) search.set('limit', params.limit);
        if (params.userId) search.set('userId', params.userId);
        if (params.status) search.set('status', params.status);
        const suffix = search.toString();
        return api.get(`/api/admin/videos${suffix ? `?${suffix}` : ''}`);
    },
    getAdminUsersList: () => api.get('/api/admin/users-list'),
    getAdminNotificationsStats: () => api.get('/api/admin/notifications-stats'),
    getAdminAppUpdate: () => api.get('/api/admin/app-update'),
    updateAdminAppUpdate: (data) => api.patch('/api/admin/app-update', data),
    getAdminAllGeneratedVideos: (params = {}) => {
        const search = new URLSearchParams();
        if (params.page) search.set('page', params.page);
        if (params.limit) search.set('limit', params.limit);
        const suffix = search.toString();
        return api.get(`/api/admin/generated-videos${suffix ? `?${suffix}` : ''}`);
    },
    updateVideoStatus: (videoId, status) => 
        api.patch(`/api/admin/videos/${videoId}/status`, { status }),
    addVideoCut: (videoId, cut) => 
        api.post(`/api/admin/videos/${videoId}/cuts`, cut),
    removeVideoCut: (videoId, cutId) => 
        api.delete(`/api/admin/videos/${videoId}/cuts/${cutId}`),
    hideAllCutsForVideo: (videoId) =>
        api.post(`/api/admin/videos/${videoId}/hide-all-cuts`),
    uploadGeneratedContent: (userId, formData) => 
        api.post(`/api/admin/users/${userId}/content`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }),
    updateUserProfile: (userId, profileData) => 
        api.patch(`/api/admin/users/${userId}/profile`, profileData, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }),

    // Generated Videos
    getUserGeneratedVideos: (userId) => {
        // If userId is provided, use admin endpoint
        if (userId) {
            return api.get(`/api/admin/users/${userId}/generated-videos`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
        }
        // Otherwise use regular user endpoint
        return api.get('/api/videos/generated-videos', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    uploadGeneratedVideo: (userId, formData) => {
        return api.post(`/api/admin/users/${userId}/generated-videos`, formData, {
            headers: { 
                Authorization: `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'multipart/form-data'
            }
        });
    },

    deleteGeneratedVideo: (videoId) => {
        return api.delete(`/api/admin/generated-videos/${videoId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    // Get pre-signed URL for video download
    getVideoDownloadUrl: (videoId, { admin = false } = {}) => {
        const url = admin ? `/api/admin/videos/${videoId}/download-url` : `/api/videos/${videoId}/download-url`;
        return api.get(url, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
    },

    // Get pre-signed URL for script video download (admin)
    getScriptVideoDownloadUrl: (scriptId) => {
        return api.get(`/api/admin/script/${scriptId}/download-url`, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
    },

    // Stream and force download for script video (admin)
    downloadScriptVideo: (scriptId) => {
        return api.get(`/api/admin/script/${scriptId}/download`, {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            responseType: 'blob'
        });
    },

    // Update video reaction (like/dislike)
    updateVideoReaction: (videoId, reaction) => {
        return api.patch(`/api/videos/generated-videos/${videoId}/reaction`, { reaction }, {
            headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
        });
    },

    // Generate video scripts
    generateVideoScripts: (data) => {
        return api.post('/api/admin/generate-scripts', data, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    generateVideoScriptsAsync: (data) => {
        return api.post('/api/admin/generate-scripts-async', data, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    checkScriptsStatus: (userId) => {
        return api.get(`/api/admin/scripts-status/${userId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    // Generate content strategy
    generateContentStrategy: (data) => {
        return api.post('/api/admin/generate-content-strategy', data, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    // Regenerate a specific script
    regenerateScript: (scriptId, regenerationPrompt) => {
        return api.post(`/api/admin/regenerate-script/${scriptId}`, { regenerationPrompt }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    // Regenerate description for a script
    regenerateDescription: (scriptId, regenerationPrompt) => {
        return api.post(`/api/admin/regenerate-description/${scriptId}`, { regenerationPrompt }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    // User Studio routes (non-admin)
    studioGenerateScriptsAsync: ({ hooks, includePublishedContext = false, creativityLevel = 3 }) => {
        return api.post('/api/studio/generate-scripts-async', { hooks, includePublishedContext, creativityLevel }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioScriptsStatus: () => {
        return api.get('/api/studio/scripts-status', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioGetScripts: () => {
        return api.get('/api/studio/scripts', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioRegenerateScript: (scriptId, regenerationPrompt) => {
        return api.post(`/api/studio/regenerate-script/${scriptId}`, { regenerationPrompt }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioGenerateVoiceover: (scriptId, voiceSettings = null, voiceId = null) => {
        console.log('[API] studioGenerateVoiceover called with:', { scriptId, voiceSettings, voiceId });
        return api.post(`/api/studio/generate-voiceover/${scriptId}`, { voiceSettings, voiceId }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioGenerateVideo: (payload) => {
        return api.post('/api/studio/generate-video', payload, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioCheckVideoStatus: (scriptId) => {
        return api.get(`/api/studio/video-status/${scriptId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioPublishVideo: (scriptId) => {
        return api.post(`/api/studio/publish-video/${scriptId}`, {}, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioUpdateScriptVersion: (scriptId, selectedVersion) => {
        return api.patch(`/api/studio/script/${scriptId}/version`, { selectedVersion }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioUpdateScriptContent: (scriptId, updates) => {
        return api.patch(`/api/studio/script/${scriptId}/content`, updates, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioVideoDownloadUrl: (scriptId) => {
        return api.get(`/api/studio/video-download-url/${scriptId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 10000
        });
    },
    studioGetAvailableHooks: () => {
        return api.get('/api/studio/available-hooks', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    // Studio-specific routes for voices, music, and fonts
    studioGetVoices: () => {
        return api.get('/api/studio/voices', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioGetMusic: () => {
        return api.get('/api/studio/music', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioGetFonts: () => {
        return api.get('/api/studio/fonts', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    studioUpdateProfile: (profileData) => {
        return api.patch('/api/studio/profile', profileData, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    // Generate voiceover for a script
    generateVoiceover: (scriptId, voiceSettings = null, voiceId = null) => {
        return api.post(`/api/admin/generate-voiceover/${scriptId}`, { 
            voiceSettings,
            voiceId 
        }, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    // Generate video with cuts and voiceover (supports optional cutVideoTag)
    generateVideo: (data) => {
        // data may include: { scriptId, autoCutEnabled, selectedCutId, selectedMusicId, customFontUrl, cutVideoTag }
        return api.post('/api/admin/generate-video', data, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },
    checkVideoStatus: (scriptId) => {
        return api.get(`/api/admin/video-status/${scriptId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    // Publish video to user's generated videos
    publishVideo: (videoId) => {
        return api.post(`/api/admin/publish-video/${videoId}`, {}, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    // Get scripts for a user
    getUserScripts: (userId) => {
        return api.get(`/api/admin/scripts/${userId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    // Get user cuts
    getUserCuts: (userId) => {
        return api.get(`/api/admin/cuts/${userId}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    },

    // Script management
    deleteScript: (scriptId) => api.delete(`/api/admin/script/${scriptId}`),
    deleteAllScripts: (userId) => api.delete(`/api/admin/scripts/${userId}`),
    updateScript: (scriptId, content, description, contentAlt) => api.patch(`/api/admin/script/${scriptId}`, { 
        content, 
        description,
        ...(contentAlt !== undefined && { contentAlt })
    }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }),
    updateScriptVersion: (scriptId, selectedVersion) => api.patch(`/api/admin/script/${scriptId}/version`, { selectedVersion }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }),

    // Persist admin selections for script (cut/music)
    updateScriptSelections: (scriptId, payload) => api.patch(`/api/admin/script/${scriptId}/selections`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }),

    // Music management
    uploadMusic: (formData) => api.post('/api/admin/music/upload', formData, {
        headers: { 
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
        },
        timeout: 600000, // 10 minutes for large music files
        maxContentLength: Infinity,
        maxBodyLength: Infinity
    }),
    getGlobalMusic: () => api.get('/api/admin/music', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }),
    deleteMusic: (musicId) => api.delete(`/api/admin/music/${musicId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }),
    updateMusicMetadata: (musicId, updates) => api.put(`/api/admin/music/${musicId}`, updates, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }),

    // Fonts management
    uploadFont: (userId, formData) => api.post(`/api/admin/fonts/upload`, formData, {
        headers: { 
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
        }
    }),
    getUserFonts: (userId) => api.get(`/api/admin/fonts/${userId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }),
    deleteFont: (fontId) => api.delete(`/api/admin/fonts/${fontId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }),

    // Toggle cut visibility (hide/show)
    toggleCutVisibility: (cutId) => api.patch(`/api/admin/cuts/${cutId}/toggle-visibility`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }),

    // Admin delete video (can delete any video regardless of ownership)
    adminDeleteVideo: (videoId) => api.delete(`/api/admin/videos/${videoId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    }),

    // Workspace routes
    getWorkspaces: () => api.get('/api/workspaces'),
    getWorkspace: (workspaceId) => api.get(`/api/workspace/${workspaceId}`),
    createWorkspace: (data) => api.post('/api/workspace', data),
    updateWorkspace: (workspaceId, data) => api.patch(`/api/workspace/${workspaceId}`, data),
    uploadWorkspaceProfilePicture: (workspaceId, formData) => api.post(`/api/workspace/${workspaceId}/upload-profile-picture`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deleteWorkspace: (workspaceId) => api.delete(`/api/workspace/${workspaceId}`),
    
    // Workspace member management
    generateInviteLink: (workspaceId) => api.post(`/api/workspace/${workspaceId}/invite`),
    getWorkspaceByInviteToken: (inviteToken) => api.get(`/api/workspace/invite/${inviteToken}`),
    joinWorkspace: (inviteToken, role = 'editor') => api.post(`/api/workspace/join/${inviteToken}`, { role }),
    updateMemberRole: (workspaceId, memberId, role) => api.patch(`/api/workspace/${workspaceId}/member/${memberId}/role`, { role }),
    removeMember: (workspaceId, memberId) => api.delete(`/api/workspace/${workspaceId}/member/${memberId}`),
    leaveWorkspace: (workspaceId) => api.delete(`/api/workspace/${workspaceId}/leave`),

    // Workspace Video Management routes
    getWorkspaceVideos: (workspaceId, params) => {
        const search = new URLSearchParams();
        if (params?.page) search.set('page', params.page);
        if (params?.pageSize) search.set('pageSize', params.pageSize);
        else if (params?.limit) search.set('pageSize', params.limit);
        const suffix = search.toString();
        return api.get(`/api/workspace/${workspaceId}/videos${suffix ? `?${suffix}` : ''}`);
    },
    getWorkspaceGeneratedVideos: (workspaceId, params) => {
        const search = new URLSearchParams();
        if (params?.page) search.set('page', params.page);
        if (params?.pageSize) search.set('pageSize', params.pageSize);
        const suffix = search.toString();
        return api.get(`/api/workspace/${workspaceId}/generated-videos${suffix ? `?${suffix}` : ''}`);
    },
    getWorkspaceTags: (workspaceId) => api.get(`/api/workspace/${workspaceId}/tags`),
    uploadWorkspaceVideo: (workspaceId, formData, config = {}) => api.post(`/api/workspace/${workspaceId}/upload`, formData, {
        headers: {
            'Accept': 'application/json'
        },
        ...config
    }),
    getWorkspaceUploadUrl: (workspaceId, fileName, fileType, fileSize) => 
        api.post(`/api/workspace/${workspaceId}/upload-url`, { fileName, fileType, fileSize }),
    getWorkspaceMultipartUrl: (workspaceId, key, uploadId, partNumber) =>
        api.post(`/api/workspace/${workspaceId}/multipart-url`, { key, uploadId, partNumber }),
    completeWorkspaceMultipart: (workspaceId, key, uploadId, parts) =>
        api.post(`/api/workspace/${workspaceId}/complete-multipart`, { key, uploadId, parts }),
    abortWorkspaceMultipart: (workspaceId, key, uploadId) =>
        api.post(`/api/workspace/${workspaceId}/abort-multipart`, { key, uploadId }),
    confirmWorkspaceUpload: (workspaceId, key, videoUrl, title, description, tags, options = {}) =>
        api.post(`/api/workspace/${workspaceId}/confirm-upload`, {
            key,
            videoUrl,
            title,
            description,
            tags,
            ...(Array.isArray(options.pendingCutWindows) && options.pendingCutWindows.length
                ? { pendingCutWindows: options.pendingCutWindows }
                : {}),
            ...(options.skipAutoCuts ? { skipAutoCuts: true } : {}),
            ...(Number.isFinite(options.durationSec) ? { durationSec: options.durationSec } : {}),
        }),
    extractWorkspaceCutWindows: (workspaceId, videoId, windows) =>
        api.post(`/api/workspace/${workspaceId}/videos/${videoId}/extract-cut-windows`, { windows }),
    uploadAdminWorkspaceVideo: (workspaceId, formData, config = {}) => api.post(`/api/admin/workspaces/${workspaceId}/upload`, formData, {
        headers: {
            'Accept': 'application/json'
        },
        ...config
    }),
    addWorkspaceVideoTag: (workspaceId, videoId, tag) => 
        api.post(`/api/workspace/${workspaceId}/videos/${videoId}/tags`, { tag }),
    removeWorkspaceVideoTag: (workspaceId, videoId, tag) => 
        api.delete(`/api/workspace/${workspaceId}/videos/${videoId}/tags/${encodeURIComponent(tag)}`),
    updateWorkspaceVideoDescription: (workspaceId, videoId, description) => 
        api.put(`/api/workspace/${workspaceId}/videos/${videoId}/description`, { description }),
    updateWorkspaceVideoTitle: (workspaceId, videoId, title) =>
        api.patch(`/api/workspace/${workspaceId}/videos/${videoId}`, { title }),
    deleteWorkspaceVideo: (workspaceId, videoId) => 
        api.delete(`/api/workspace/${workspaceId}/videos/${videoId}`),
    getWorkspaceVideoDownloadUrl: (workspaceId, videoId) => 
        api.get(`/api/workspace/${workspaceId}/videos/${videoId}/download`),
    getWorkspaceGeneratedVideoDownloadUrl: (workspaceId, videoId) => 
        api.get(`/api/workspace/${workspaceId}/generated-videos/${videoId}/download`),
    deleteWorkspaceGeneratedVideo: (workspaceId, videoId) => 
        api.delete(`/api/workspace/${workspaceId}/generated-videos/${videoId}`),
    updateWorkspaceGeneratedVideoTitle: (workspaceId, videoId, title) =>
        api.patch(`/api/workspace/${workspaceId}/generated-videos/${videoId}`, { title }),
    importInstagramVideos: (workspaceId, username, limit = 20) => 
        api.post(`/api/workspace/${workspaceId}/import-instagram-videos`, { username, limit }),
    getInstagramImportStatus: (workspaceId) => 
        api.get(`/api/workspace/${workspaceId}/instagram-import-status`),
    
    // Thumbnails maker (public endpoints; mount order on server must allow unauthenticated access)
    startProfileThumbnailsFetch: (platform, username, limit = 20) =>
        publicApi.post('/api/thumbnails/profile/start', { platform, username, limit }),
    getProfileThumbnailsStatus: (jobId) =>
        publicApi.get('/api/thumbnails/profile/status', { params: { jobId } }),
    suggestThumbnailText: (payload) =>
        publicApi.post('/api/thumbnails/suggest', payload),
    generateThumbnailImage: ({ prompt, aspectRatio = '9:16', imageUrl = null }) =>
        publicApi.post('/api/thumbnails/generate-image', { prompt, aspectRatio, imageUrl }),
    getProxiedImageUrl: (url) =>
        `${(process.env.REACT_APP_API_URL || 'https://proto-backend-d2c1638486b9.herokuapp.com')}/api/thumbnails/proxy-image?url=${encodeURIComponent(url)}`,
    updateWorkspaceVideoReaction: (workspaceId, videoId, reaction) => 
        api.put(`/api/workspace/${workspaceId}/generated-videos/${videoId}/reaction`, { reaction }),
    addWorkspaceVideoCut: (workspaceId, videoId, cut) => 
        api.post(`/api/workspace/${workspaceId}/videos/${videoId}/cuts`, cut),
    removeWorkspaceVideoCut: (workspaceId, videoId, cutId) => 
        api.delete(`/api/workspace/${workspaceId}/videos/${videoId}/cuts/${cutId}`),

    // Workspace Video Studio routes
    getWorkspaceHooks: (workspaceId) => api.get(`/api/workspace/${workspaceId}/hooks`),
    getWorkspaceVoices: (workspaceId) => api.get(`/api/workspace/${workspaceId}/voices`),
    getWorkspaceMusic: (workspaceId) => api.get(`/api/workspace/${workspaceId}/music`),
    toggleMusicStar: (workspaceId, musicId) => api.post(`/api/workspace/${workspaceId}/music/${musicId}/toggle-star`),
    getWorkspaceFonts: (workspaceId) => api.get(`/api/workspace/${workspaceId}/fonts`),
    updateWorkspaceProfile: (workspaceId, profileData) => 
        api.put(`/api/workspace/${workspaceId}/profile`, profileData),
    
    // Workspace Script Management
    generateWorkspaceScriptsAsync: (workspaceId, data) => 
        api.post(`/api/workspace/${workspaceId}/generate-scripts-async`, data),
    getWorkspaceScriptsStatus: (workspaceId) => 
        api.get(`/api/workspace/${workspaceId}/scripts-status`),
    getWorkspaceScripts: (workspaceId, params = {}) => {
        const search = new URLSearchParams();
        if (params.lite === true || params.lite === '1' || params.lite === 1) search.set('lite', '1');
        if (params.since) search.set('since', params.since);
        if (params.type) search.set('type', params.type);
        if (params.limit != null) search.set('limit', String(params.limit));
        if (params.page != null) search.set('page', String(params.page));
        const query = search.toString();
        return api.get(`/api/workspace/${workspaceId}/scripts${query ? `?${query}` : ''}`);
    },
    getWorkspaceScript: (workspaceId, scriptId) =>
        api.get(`/api/workspace/${workspaceId}/script/${scriptId}`),
    generateWorkspaceVoiceover: (workspaceId, scriptId, data) => 
        api.post(`/api/workspace/${workspaceId}/generate-voiceover/${scriptId}`, data, { timeout: 180000 }),
    getWorkspaceStudioVideoDownloadUrl: (workspaceId, scriptId, options = {}) => 
        api.get(`/api/workspace/${workspaceId}/video-download-url/${scriptId}`, options),
    publishWorkspaceVideo: (workspaceId, scriptId, data) => 
        api.post(`/api/workspace/${workspaceId}/publish-video/${scriptId}`, data),
    updateWorkspaceScriptVersion: (workspaceId, scriptId, version) => 
        api.patch(`/api/workspace/${workspaceId}/script/${scriptId}/version`, { selectedVersion: version }),
    updateWorkspaceScriptContent: (workspaceId, scriptId, version, content, extra = {}) => {
        const body = { ...extra };
        if (content !== undefined && content !== null) {
            body[version === 'main' ? 'content' : version === 'alt' ? 'contentAlt' : 'contentThird'] = content;
        }
        return api.patch(`/api/workspace/${workspaceId}/script/${scriptId}/content`, body);
    },
    regenerateWorkspaceScript: (workspaceId, scriptId, data = {}) => 
        api.post(`/api/workspace/${workspaceId}/regenerate-script/${scriptId}`, data),
    regenerateWorkspaceDescription: (workspaceId, scriptId, data = {}) =>
        api.post(`/api/workspace/${workspaceId}/regenerate-description/${scriptId}`, data),
    generateCarouselSlides: (workspaceId, data = {}) =>
        api.post(`/api/workspace/${workspaceId}/generate-carousel-slides`, data, { timeout: 120000 }),
    translateWorkspaceScript: (workspaceId, scriptId, data) => 
        api.post(`/api/workspace/${workspaceId}/translate-script/${scriptId}`, data),
    getWorkspaceTranslationStatus: (workspaceId, translationId) => 
        api.get(`/api/workspace/${workspaceId}/translation-status/${translationId}`),
    saveCustomScript: (workspaceId, data) => 
        api.post(`/api/workspace/${workspaceId}/save-custom-script`, data),
    generateWorkspaceVideo: (workspaceId, scriptId, data) => 
        api.post(`/api/workspace/${workspaceId}/generate-video`, { scriptId, ...data }),
    planWorkspaceSfx: (workspaceId, data) =>
        api.post(`/api/workspace/${workspaceId}/plan-sfx`, data),
    getWorkspaceSfxCatalog: (workspaceId) =>
        api.get(`/api/workspace/${workspaceId}/sfx-catalog`),
    requestWorkspaceNleHlsPlaylist: (workspaceId, data) =>
        api.post(`/api/workspace/${workspaceId}/nle-hls-playlist`, data || {}),
    getWorkspaceVideoStatus: (workspaceId, scriptId) => 
        api.get(`/api/workspace/${workspaceId}/video-status/${scriptId}`),

    // Workspace Cuts routes
    getWorkspaceCuts: (workspaceId, params = {}) => {
        const search = new URLSearchParams();
        if (params.page) search.set('page', params.page);
        if (params.pageSize) search.set('pageSize', params.pageSize);
        if (params.totalOnly) search.set('totalOnly', params.totalOnly);
        if (params.includeTags) search.set('includeTags', params.includeTags);
        if (params.excludeTags) search.set('excludeTags', params.excludeTags);
        if (params.ids) search.set('ids', params.ids);
        if (params.videoIds) search.set('videoIds', params.videoIds);
        const query = search.toString();
        return api.get(`/api/workspace/${workspaceId}/cuts${query ? `?${query}` : ''}`);
    },
    toggleWorkspaceCutVisibility: (workspaceId, cutId) => 
        api.patch(`/api/workspace/${workspaceId}/cuts/${cutId}/toggle-visibility`),
    getVideoCutsGenerationStatus: (workspaceId, videoId) =>
        api.get(`/api/workspace/${workspaceId}/videos/${videoId}/cuts-status`),

    // Admin Workspace routes
    getAdminWorkspaceRankings: (params = {}) => {
        const search = new URLSearchParams();
        if (params.metric) search.set('metric', params.metric);
        if (params.period) search.set('period', params.period);
        const suffix = search.toString();
        return api.get(`/api/admin/analytics/workspace-rankings${suffix ? `?${suffix}` : ''}`);
    },
    getAdminWorkspaces: () => api.get('/api/admin/workspaces'),
    getAdminWorkspaceDetails: (workspaceId) => api.get(`/api/admin/workspaces/${workspaceId}`),
    getAdminWorkspaceVideos: (workspaceId, params = {}) => {
        const search = new URLSearchParams();
        if (params.page) search.set('page', params.page);
        if (params.limit) search.set('limit', params.limit);
        return api.get(`/api/admin/workspaces/${workspaceId}/videos?${search.toString()}`);
    },
    getAdminWorkspaceCuts: (workspaceId) => api.get(`/api/admin/workspaces/${workspaceId}/cuts`),
    getAdminWorkspaceGeneratedVideos: (workspaceId) => api.get(`/api/admin/workspaces/${workspaceId}/generated-videos`),
    updateAdminWorkspace: (workspaceId, data) => api.put(`/api/admin/workspaces/${workspaceId}`, data),
    deleteAdminWorkspace: (workspaceId) => api.delete(`/api/admin/workspaces/${workspaceId}`),
    getAdminVideosWithWorkspaces: (params = {}) => {
        const search = new URLSearchParams();
        if (params.page) search.set('page', params.page);
        if (params.limit) search.set('limit', params.limit);
        if (params.workspaceId) search.set('workspaceId', params.workspaceId);
        if (params.status) search.set('status', params.status);
        return api.get(`/api/admin/videos-with-workspaces?${search.toString()}`);
    },

    // Admin Workspace Profile Management
    updateAdminWorkspaceProfile: (workspaceId, profileData) => 
        api.put(`/api/admin/workspaces/${workspaceId}/profile`, profileData),

    // Admin Workspace Member Management
    addAdminWorkspaceMember: (workspaceId, userId, role = 'editor') => 
        api.post(`/api/admin/workspaces/${workspaceId}/members`, { userId, role }),
    updateAdminWorkspaceMemberRole: (workspaceId, userId, role) => 
        api.put(`/api/admin/workspaces/${workspaceId}/members/${userId}`, { role }),
    removeAdminWorkspaceMember: (workspaceId, userId) => 
        api.delete(`/api/admin/workspaces/${workspaceId}/members/${userId}`),
    generateAdminWorkspaceInviteLink: (workspaceId) => 
        api.post(`/api/admin/workspaces/${workspaceId}/invite-link`),

    // Admin Workspace Creation
    createAdminWorkspace: (name, description, adminId) => 
        api.post('/api/admin/workspaces', { name, description, adminId }),
    getAdminUsersForWorkspace: () => api.get('/api/admin/users-for-workspace'),
    uploadWorkspaceAudioForVoiceCloning: (workspaceId, formData) => 
        api.post(`/api/workspace/${workspaceId}/upload-audio-for-voice-cloning`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }),
    saveWorkspaceRecording: (workspaceId, formData) => 
        api.post(`/api/workspace/${workspaceId}/save-recording`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }),
    deleteWorkspaceRecording: (workspaceId, recordingId) => 
        api.delete(`/api/workspace/${workspaceId}/recording/${recordingId}`),

    // No-script video generation
    generateNoScriptVideo: (workspaceId, data) =>
        api.post(`/api/workspace/${workspaceId}/generate-no-script-video`, data),
    getNoScriptVideoStatus: (workspaceId) =>
        api.get(`/api/workspace/${workspaceId}/no-script-video-status`),

    // AI suggestion for onboarding
    getAISuggestion: async (field, companyPurpose) => {
        const response = await api.post('/api/ai-suggestion', {
            field,
            companyPurpose
        });
        return response.data;
    },

    // Complete onboarding
    completeOnboarding: async () => {
        const response = await api.post('/api/complete-onboarding');
        return response.data;
    },

    // Save onboarding survey answers (referral source, etc.)
    updateOnboardingAnswers: async (data) => {
        const response = await api.post('/api/onboarding-answers', data);
        return response.data;
    },

    // Reset onboarding
    resetOnboarding: async () => {
        const response = await api.post('/api/reset-onboarding');
        return response.data;
    },

    // Cancel upload
    cancelUpload: async (workspaceId, uploadId) => {
        const response = await api.post(`/api/workspace/${workspaceId}/cancel-upload/${uploadId}`);
        return response.data;
    },

    // Generate cuts for a workspace video
    generateVideoCuts: async (workspaceId, videoId) => {
        const response = await api.post(`/api/workspace/${workspaceId}/videos/${videoId}/generate-cuts`);
        return response.data;
    },
    // Generate shorts using Vizard API
    generateVideoShorts: async (workspaceId, videoId) => {
        const response = await api.post(`/api/workspace/${workspaceId}/videos/${videoId}/generate-shorts`);
        return response.data;
    },

    // Get cuts generation status for a workspace video
    getVideoCutsStatus: async (workspaceId, videoId) => {
        const response = await api.get(`/api/workspace/${workspaceId}/videos/${videoId}/cuts-status`);
        return response.data;
    },

    // GetLate (Late API) — workspace-scoped: one profile per workspace, account ids stored on workspace
    connectInstagram: async (workspaceId) => {
        const response = await api.post('/api/getlate/connect/instagram', { workspaceId });
        return response.data;
    },
    connectTikTok: async (workspaceId) => {
        const response = await api.post('/api/getlate/connect/tiktok', { workspaceId });
        return response.data;
    },
    finalizeGetLateConnection: async (workspaceId, platform) => {
        const response = await api.post('/api/getlate/finalize', { workspaceId, platform });
        return response.data;
    },
    getConnectedAccounts: async (workspaceId) => {
        const response = await api.get('/api/getlate/accounts', { params: { workspaceId } });
        return response.data;
    },
    publishToInstagram: async (workspaceId, videoUrl, caption, scheduleAt = null, publishNow = null, isDraft = false) => {
        const response = await api.post('/api/getlate/publish/instagram', {
            workspaceId,
            videoUrl,
            caption,
            scheduleAt,
            publishNow,
            isDraft
        });
        return response.data;
    },
    publishToTikTok: async (workspaceId, videoUrl, caption, scheduleAt = null, publishNow = null, isDraft = false) => {
        const response = await api.post('/api/getlate/publish/tiktok', {
            workspaceId,
            videoUrl,
            caption,
            scheduleAt,
            publishNow,
            isDraft
        });
        return response.data;
    },
    getPublishStatus: async (jobId) => {
        const response = await api.get(`/api/getlate/publish/status/${jobId}`);
        return response.data;
    },
    getWorkspaceAutomationItems: (workspaceId, params = {}) => {
        const search = new URLSearchParams();
        if (params.status) search.set('status', params.status);
        const q = search.toString();
        return api.get(`/api/workspace/${workspaceId}/automation/items${q ? `?${q}` : ''}`);
    },
    runWorkspaceAutomation: (workspaceId) =>
        api.post(`/api/workspace/${workspaceId}/automation/run`),
    approveWorkspaceAutomationItem: (workspaceId, itemId) =>
        api.post(`/api/workspace/${workspaceId}/automation/items/${itemId}/approve`),
    rejectWorkspaceAutomationItem: (workspaceId, itemId) =>
        api.post(`/api/workspace/${workspaceId}/automation/items/${itemId}/reject`),
    scheduleWorkspaceAutomationItem: (workspaceId, itemId, data) =>
        api.post(`/api/workspace/${workspaceId}/automation/items/${itemId}/schedule`, data),
    // Public calendar (no auth): workspace name and scheduled posts for anyone with the link
    getCalendarWorkspaceInfo: async (workspaceId) => {
        const response = await analyzeApi.get(`/api/calendar/${workspaceId}/info`);
        return response.data;
    },
    getScheduledPosts: async (workspaceId, from = null, to = null) => {
        const params = {};
        if (from) params.from = from;
        if (to) params.to = to;
        const response = await analyzeApi.get(`/api/calendar/${workspaceId}/scheduled-posts`, { params });
        return response.data;
    }
};

// Assign all methods to the api object
Object.assign(api, apiMethods);

export default api;